// One lead on the weekly intro card: who they are, how much the customer has
// written with them (counts and a date, never content), the editable intro
// and Yes / Skip; after Yes, how the intro is doing.
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import SendIcon from "@mui/icons-material/Send";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import {
	type IntroLead,
	type IntroStatus,
	sendIntro,
	skipIntro,
} from "../../api/intro-program/intro-program";
import { formatMonthYear } from "../../utils/date-formatting";

const STATUS_CHIPS: Record<
	Exclude<IntroStatus, "pending" | "skipped">,
	{
		label: string;
		color: "info" | "success" | "default";
		variant: "outlined" | "filled";
	}
> = {
	sent: { label: "Waiting for reply", color: "info", variant: "outlined" },
	replied: { label: "Replied", color: "success", variant: "outlined" },
	converted: {
		label: "Trying Artemis — thank you!",
		color: "success",
		variant: "filled",
	},
	closed: { label: "No reply, closed", color: "default", variant: "outlined" },
};

const errorText = (err: unknown): string =>
	err instanceof Error ? err.message : String(err);

const initials = (name: string): string =>
	name
		.split(" ")
		.filter((part) => part !== "")
		.slice(0, 2)
		.map((part) => part[0].toUpperCase())
		.join("");

const LeadIdentity = ({ lead }: { lead: IntroLead }) => (
	<Stack direction="row" spacing={2} sx={{ minWidth: 0 }}>
		<Avatar data-component="intro-lead-avatar">{initials(lead.name)}</Avatar>
		<Box sx={{ minWidth: 0 }}>
			<Link
				data-component="intro-lead-name"
				href={lead.linkedin_url ?? undefined}
				target="_blank"
				rel="noopener"
				color="textPrimary"
				variant="subtitle1"
				sx={{ fontWeight: 600 }}
			>
				{lead.name}
			</Link>
			<Typography variant="body2" color="textSecondary">
				{lead.title !== null
					? [lead.title, lead.company].filter(Boolean).join(", ")
					: lead.headline}
			</Typography>
			{lead.tie !== null ? (
				<Typography
					variant="body2"
					color="textSecondary"
					data-component="intro-lead-history"
				>
					{lead.tie.count} messages via {lead.tie.channel}
					{lead.tie.last_at !== null
						? `, last ${formatMonthYear(lead.tie.last_at)}`
						: ""}
				</Typography>
			) : null}
		</Box>
	</Stack>
);

/** After Yes: on its way, or sent and how it went. */
const SentState = ({
	lead,
	staffFirstName,
}: {
	lead: IntroLead;
	staffFirstName: string;
}) => {
	if (lead.send_state === "queued")
		return (
			<Alert
				data-component="intro-lead-sending"
				severity="info"
				icon={<CircularProgress size={18} />}
				sx={{ py: 0 }}
			>
				Artemis Helper is sending it from your LinkedIn to {lead.first_name} and{" "}
				{staffFirstName}
			</Alert>
		);
	const chip = STATUS_CHIPS[lead.status as keyof typeof STATUS_CHIPS];
	return (
		<Stack spacing={1} data-component="intro-lead-sent">
			<Alert severity="success" sx={{ py: 0 }}>
				Sent from your LinkedIn to {lead.first_name} and {staffFirstName}
			</Alert>
			<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
				<Chip
					data-component="intro-lead-status-chip"
					data-status={lead.status}
					size="small"
					color={chip.color}
					variant={chip.variant}
					label={chip.label}
				/>
				{lead.conversation_url !== null ? (
					<Link
						data-component="intro-lead-open-conversation"
						href={lead.conversation_url}
						target="_blank"
						rel="noopener"
						variant="body2"
					>
						Open conversation
					</Link>
				) : null}
			</Stack>
		</Stack>
	);
};

export const IntroLeadRow = ({
	lead,
	staffFirstName,
	onChange,
}: {
	lead: IntroLead;
	staffFirstName: string;
	onChange: (lead: IntroLead) => void;
}) => {
	const canSend = lead.can_send;
	const [text, setText] = useState(lead.intro_text);
	const [busy, setBusy] = useState<"yes" | "skip" | null>(null);
	const [error, setError] = useState("");
	const answerable = lead.status === "pending" && lead.send_state !== "queued";

	const answer = async (kind: "yes" | "skip") => {
		setBusy(kind);
		setError("");
		try {
			onChange(
				kind === "yes"
					? await sendIntro(lead.id, text)
					: await skipIntro(lead.id)
			);
		} catch (err) {
			setError(errorText(err));
		} finally {
			setBusy(null);
		}
	};

	return (
		<Box
			data-component="intro-lead-row"
			data-status={lead.status}
			data-send-state={lead.send_state}
			sx={{
				display: "grid",
				gridTemplateColumns: { xs: "1fr", md: "380px 1fr 320px" },
				gap: 2,
				alignItems: "center",
				py: 2,
				borderTop: 1,
				borderColor: "divider",
			}}
		>
			<Box>
				<LeadIdentity lead={lead} />
				<Stack
					direction="row"
					spacing={0.75}
					sx={{ alignItems: "center", mt: 1, ml: 7 }}
					data-component="intro-lead-gift"
				>
					<CardGiftcardIcon fontSize="small" color="success" />
					<Typography variant="body2" color="success.main">
						Through you, {lead.first_name} gets the first month of Artemis
						free
					</Typography>
				</Stack>
			</Box>
			<TextField
				data-component="intro-text"
				label="Intro, sent in your name"
				size="small"
				multiline
				minRows={2}
				value={text}
				onChange={(event) => setText(event.target.value)}
				disabled={!answerable || !canSend || busy !== null}
				fullWidth
			/>
			{answerable ? (
				<Stack spacing={1}>
					{lead.send_state === "failed" ? (
						<Alert
							severity="error"
							sx={{ py: 0 }}
							data-component="intro-send-failed"
						>
							Send failed: {lead.send_error}
						</Alert>
					) : null}
					{error !== "" ? (
						<Alert
							severity="error"
							sx={{ py: 0 }}
							data-component="intro-answer-error"
						>
							{error}
						</Alert>
					) : null}
					<Stack
						direction="row"
						spacing={1}
						sx={{ justifyContent: "flex-end" }}
					>
						<Button
							data-component="intro-lead-skip"
							color="inherit"
							disabled={busy !== null}
							onClick={() => answer("skip")}
						>
							Skip
						</Button>
						<Button
							data-component="intro-lead-yes"
							variant="contained"
							startIcon={
								busy === "yes" ? (
									<CircularProgress size={16} color="inherit" />
								) : (
									<SendIcon />
								)
							}
							disabled={busy !== null || !canSend || text.trim() === ""}
							onClick={() => answer("yes")}
							sx={{ whiteSpace: "nowrap" }}
						>
							{lead.send_state === "failed"
								? "Try again"
								: "Yes, send intro"}
						</Button>
					</Stack>
				</Stack>
			) : (
				<SentState lead={lead} staffFirstName={staffFirstName} />
			)}
		</Box>
	);
};
